import {faTriangleExclamation} from '@fortawesome/pro-regular-svg-icons';
import {faCircleInfo} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Divider, Flex, Tooltip} from 'antd';
import {useCallback, useState} from 'react';
import {ExtractRouteParams, generatePath, useParams} from 'react-router';

import {appRoute} from '../../../../app-route';
import {AlertFallback} from '../../../../component/alert-fallback/alert-fallback';
import {Meta} from '../../../../component/meta/meta';
import {BreadCrumbs} from '../../../../layout/bread-crumbs/bread-crumbs';
import {Page} from '../../../../layout/page/page';
import {MainPageContainer} from '../../../../layout/page-card/main-page-container';
import {PageCard} from '../../../../layout/page-card/page-card';
import {PageHeader} from '../../../../layout/page-header/page-header';
import {Spinner} from '../../../../layout/spinner/spinner';
import {useDomainConfig} from '../../../../provider/domain-config/domain-config-hook';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {Locale} from '../../../../provider/locale/localization';
import {useModal} from '../../../../provider/modal/modal-hook';
import {HelpLinkFeatureFlagEnum} from '../../../../service/feature-flags/feature-flag-const';
import {useFeatureFlag} from '../../../../service/feature-flags/use-feature-flag';
import {useReviewRealtimePresence} from '../../../../service/reviews/review-realtime-presence/review-realtime-presence-context';
import {ReviewPresenceBlockedActionEnum} from '../../../../service/reviews/review-realtime-presence/review-realtime-presence-type';
import {useReviewItem} from '../../../../service/reviews/reviews';
import {useReviewsAbuseReason} from '../../../../service/reviews/reviews-abuse-hook';
import {useReviewsCatalogList} from '../../../../service/reviews/reviews-catalogs';
import {
    ReviewsGmbCclStatusEnum,
    useReviewsGmbCclStatuses,
} from '../../../../service/reviews/reviews-gmb-confirmation-hook';
import {useToggleReviewVisibilityMutation} from '../../../../service/reviews/reviews-hide';
import {useReviewLogsList} from '../../../../service/reviews/reviews-logs';
import {NoteFormType, useNotesMutation} from '../../../../service/reviews/reviews-notes';
import {useReviewsSuggestions} from '../../../../service/reviews/reviews-suggestions';
import {ReviewDataType} from '../../../../service/reviews/reviews-type';
import {ProvidersIdsEnum} from '../../../../util/type';
import {useUrl} from '../../../../util/url-hook/url-hook';
import {useReviewActivityHook} from '../review-activity-provider/review-activity-provider';
import {ReviewAbusePopover} from '../review-list-page-content/review-list/review/reply-form/abuse-tag/review-abuse-popover';
import {NewAbuseModal} from '../review-list-page-content/review-list/review/reply-form/new-abuse-modal/new-abuse-modal';
import {
    isAbuseAvailable,
    isGoogleCatalog,
} from '../review-list-page-content/review-list/review/reply-form/new-abuse-modal/new-abuse-modal-const';
import {ReviewHistoryModal} from '../review-list-page-content/review-list/review/review-header/history-modal/history-modal';
import {NoteModal} from '../review-list-page-content/review-list/review/review-header/note-modal/note-modal';
import {ResponsibleUserModal} from '../review-list-page-content/review-list/review/review-header/responsible-user-modal/responsible-user-modal';
import {useReviewMenu} from '../review-list-page-content/review-list/review/review-menu-hook/review-menu-hook';
import {ReviewDataWithCatalogType} from '../review-list-page-content/review-list/review-list-type';
import {ReviewPresenceAction} from '../review-realtime-presence/review-presence-action/review-presence-action';
import {ReviewPresenceBlockedTooltip} from '../review-realtime-presence/review-presence-blocked-tooltip/review-presence-blocked-tooltip';
import {ReviewStatus} from '../review-status/review-status';
import {isReviewAbuseAvailableWhenVisible} from '../reviews-helper';
import {useReviewStateHook} from '../reviews-state/reviews-state-provider';
import {TagList} from '../tag-list/tag-list';

import {ReviewWrapper} from './review-wrapper/review-wrapper';
import * as styles from './new-review-item-page-content.scss';

// eslint-disable-next-line complexity, max-statements
export function NewReviewItemPageContent(): JSX.Element | null {
    const {data: reviewsCatalogList, isFetching: reviewsCatalogListIsInProgress} = useReviewsCatalogList();

    const [isOpenNoteModal, setIsOpenNoteModal] = useState<boolean>(false);
    const [isOpenHistoryModal, setIsOpenHistoryModal] = useState<boolean>(false);
    const [isOpenResponsibleUserModal, setIsOpenResponsibleUserModal] = useState<boolean>(false);
    const [isOpenAbuseModal, setIsOpenAbuseModal] = useState<boolean>(false);

    const {getLocalizedString} = useLocale();
    const {id} = useParams<ExtractRouteParams<typeof appRoute.reviewsManagementReviewItem.path, string>>();
    const reviewId = Number(id);
    const {companyName} = useDomainConfig();
    const {pushUrl} = useUrl();
    const googlePlaywrightEnabled = useFeatureFlag(HelpLinkFeatureFlagEnum.googlePlaywrightReviewAbuse);

    const {isHistoryVisible} = useReviewActivityHook();
    const {
        googleMapsReviewIds,
        refreshReview,
        refreshSuggestions,
        updateReview: updateReviewInState,
    } = useReviewStateHook();
    const {refetch: loadLogs} = useReviewLogsList(Number(id), isHistoryVisible);
    const {data: reviewsAbuseReason} = useReviewsAbuseReason([reviewId]);

    const {data: review, isLoading, error, refetch: refreshReviewItem} = useReviewItem(reviewId);
    const {data: resultSuggestions} = useReviewsSuggestions([reviewId]);

    const catalog = reviewsCatalogList?.find((item) => item.id === review?.catalogId);

    const suggestion = resultSuggestions?.results?.[0];

    const {mutateAsync, isLoading: noteMutationLoading} = useNotesMutation();

    const {data: reviewGmbStatuses} = useReviewsGmbCclStatuses(googleMapsReviewIds);
    const {modal} = useModal();
    const isNeedConfirmationStatus =
        reviewGmbStatuses &&
        (reviewGmbStatuses[reviewId] === ReviewsGmbCclStatusEnum.WaitVerify ||
            reviewGmbStatuses[reviewId] === ReviewsGmbCclStatusEnum.NotVerified);

    const reviewAbuseReason = (reviewsAbuseReason || []).find((reviewReason) => reviewReason.id === reviewId) || null;

    const isHiddenReview = Boolean(review?.isHidden);
    const isAbuseVisibleWhenNotHidden = review ? isReviewAbuseAvailableWhenVisible(review) : false;
    const effectiveAbleToAbuse = isAbuseAvailable({
        isDeleted: Boolean(review?.deletedAt),
        ableToAbuse: Boolean(review?.ableToAbuse),
        catalogId: catalog?.id,
        googlePlaywrightEnabled,
    });

    const isShowExternalOptions = Boolean(catalog?.is_external_reviews) && !review?.deletedAt;

    const {mutateAsync: setReviewVisibility} = useToggleReviewVisibilityMutation({
        updateReview: updateReviewInState,
    });

    const {isLockedByOther} = useReviewRealtimePresence();
    const isPresenceLocked = isLockedByOther(reviewId);

    const menuItems = useReviewMenu({
        ableToChangeResponsibleUser: Boolean(review?.ableToChangeResponsibleUser),
        catalogId: review?.catalogId,
        isPresenceLocked,
        isReviewDeleted: Boolean(review?.deletedAt),
        isHidden: Boolean(review?.isHidden),
        isShowExternalOptions,
        isShowOpenNewTabButton: false,
        onSetHidden: async (nextHidden) => {
            await setReviewVisibility({reviewId, isHidden: nextHidden});
        },
        onSubmitFinish: refreshReviewItem,
        reviewId,
        setIsOpenHistoryModal,
        setIsOpenNoteModal,
        setIsOpenResponsibleUserModal,
        text: review?.comment || '',
    });

    const updateReview = useCallback(
        (partialReviewData: Partial<ReviewDataType>) => {
            updateReviewInState(reviewId, partialReviewData);
        },
        [reviewId, updateReviewInState]
    );

    async function onSubmit(formValues: NoteFormType) {
        return mutateAsync(
            {text: (formValues.text ?? '').trim(), reviewId},
            {
                onSuccess: async (data) => {
                    await updateReview({notes: [data, ...(review?.notes ?? [])]});

                    setIsOpenNoteModal(false);
                },
            }
        );
    }

    const handleClick = useCallback(() => {
        if (isPresenceLocked) {
            return;
        }

        // Skip the GMB card-confirmation gate for the Google Playwright flow — the form opens directly.
        if (isNeedConfirmationStatus && !(googlePlaywrightEnabled && isGoogleCatalog(catalog?.id))) {
            modal.confirm({
                icon: <FontAwesomeIcon className={styles.ReviewPage_modalIcon} icon={faCircleInfo} size="xl" />,
                maskClosable: true,
                title: <Locale stringKey="REVIEW__ANSWER__CONFIRMATION_MODAL__TITLE" />,
                content: <Locale stringKey="REVIEW__ANSWER__CONFIRMATION_MODAL__CONTENT" valueMap={{companyName}} />,
                okText: <Locale stringKey="POPUP__BUTTON__CONFIRM" />,
                cancelText: <Locale stringKey="BUTTON__CANCEL" />,
                onOk: () => {
                    pushUrl(generatePath(appRoute.sourcesAccountSyncGoogleConfirmations.path));
                },
            });
        } else {
            setIsOpenAbuseModal(true);
        }
    }, [isNeedConfirmationStatus, isPresenceLocked, googlePlaywrightEnabled, catalog?.id, modal, companyName, pushUrl]);

    const reviewWithCatalog: ReviewDataWithCatalogType | null = review ? {...review, catalog: catalog || null} : null;

    return (
        <Page isTopFilterHidden renderContainer={(children) => <MainPageContainer>{children}</MainPageContainer>}>
            <PageCard>
                <Meta title={getLocalizedString('REVIEW_ITEM_PAGE__TITLE')} />
                <BreadCrumbs
                    list={[
                        {
                            path: appRoute.reviewsManagementReviews.path,
                            titleLangKey: 'CATEGORY_NAME__REVIEWS',
                        },
                        {
                            path: generatePath(appRoute.reviewsManagementReviewItem.path, {id}),
                            titleLangKey: 'REVIEW_ITEM_PAGE__TITLE',
                        },
                    ]}
                />
                <PageHeader className={styles.ReviewPage_pageHeader}>
                    <Locale stringKey="REVIEW_ITEM_PAGE__TITLE" />
                </PageHeader>
            </PageCard>

            <div className={styles.ReviewPage_contentWrapper}>
                <Spinner isShow={isLoading} position="absolute" />

                {Boolean(error) && !isLoading && <AlertFallback />}

                {review && !error && (
                    <div className={styles.ReviewPage_content}>
                        {reviewWithCatalog && (
                            <ReviewWrapper
                                isCatalogLoading={reviewsCatalogListIsInProgress}
                                openHistoryModalHandler={() => setIsOpenHistoryModal(true)}
                                refreshReview={refreshReview}
                                refreshReviewItem={refreshReviewItem}
                                refreshSuggestions={refreshSuggestions}
                                review={reviewWithCatalog}
                                suggestion={suggestion}
                                updateReviewItem={updateReview}
                            />
                        )}
                        <div className={styles.ReviewPage_aside}>
                            <div className={styles.ReviewPage_asideContent}>
                                <div className={styles.ReviewPage_catalogLine}>
                                    <ReviewStatus
                                        brandId={review.brand.id}
                                        className={styles.ReviewPage_status}
                                        isPresenceLocked={isPresenceLocked}
                                        onChange={loadLogs}
                                        processStatus={review.processStatus}
                                        reviewId={review.id}
                                        updateReview={(_reviewId, updateData) => updateReview(updateData)}
                                    />
                                </div>
                                <Flex align="start" className={styles.ReviewPage_actionBlock} vertical>
                                    {menuItems &&
                                        menuItems?.map((item) => {
                                            const menuButton = (
                                                <Button
                                                    disabled={item.disabled}
                                                    icon={item.icon}
                                                    // eslint-disable-next-line no-undefined
                                                    onClick={item.onClick ?? undefined}
                                                    style={{width: '100%', justifyContent: 'flex-start'}}
                                                    type="text"
                                                >
                                                    {item.label}
                                                </Button>
                                            );

                                            if (item.key === 'responsible' && item.disabled) {
                                                return (
                                                    <ReviewPresenceBlockedTooltip
                                                        blockedAction={ReviewPresenceBlockedActionEnum.ResponsibleUser}
                                                        isBlocked
                                                        key={item.key}
                                                    >
                                                        {menuButton}
                                                    </ReviewPresenceBlockedTooltip>
                                                );
                                            }

                                            return (
                                                <span key={item.key} style={{display: 'block', width: '100%'}}>
                                                    {menuButton}
                                                </span>
                                            );
                                        })}
                                </Flex>
                                <Divider className={styles.ReviewPage_divider} />
                                <Flex align="start" className={styles.ReviewPage_tagsBlock} vertical>
                                    <TagList
                                        deletedAt={review.deletedAt}
                                        isPresenceLocked={isPresenceLocked}
                                        responsibleUser={review.responsibleUser}
                                        reviewId={reviewId}
                                        tags={review.tags}
                                        updateReview={(_reviewId, updateData) => updateReview(updateData)}
                                    />
                                </Flex>
                                <Divider className={styles.ReviewPage_divider} />
                                <Flex className={styles.ReviewPage_abuseBlock}>
                                    {isPresenceLocked ? (
                                        <ReviewPresenceAction />
                                    ) : (
                                        <>
                                            <ReviewAbusePopover
                                                abuseLastDate={review?.abuseLastDate || null}
                                                catalogId={catalog?.id}
                                                reviewAbuseReason={reviewAbuseReason}
                                            />
                                            {!isHiddenReview && !review.abuseLastDate && effectiveAbleToAbuse && (
                                                <Button onClick={handleClick} style={{width: '100%'}}>
                                                    <Locale stringKey="REVIEWS__REVIEW_ABUSE__HINT" />
                                                </Button>
                                            )}
                                            {!isHiddenReview &&
                                                review.catalogId !== ProvidersIdsEnum.yandex &&
                                                review.abuseLastDate &&
                                                review.ableToAbuse && (
                                                    <Tooltip
                                                        placement="topRight"
                                                        title={getLocalizedString('REVIEW__ABUSE__SEND_AGAIN')}
                                                    >
                                                        <Button
                                                            className={styles.ReviewPage_abuseButton}
                                                            onClick={handleClick}
                                                        >
                                                            <FontAwesomeIcon icon={faTriangleExclamation} />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            {isHiddenReview &&
                                                !review.deletedAt &&
                                                review.catalogId !== ProvidersIdsEnum.yandex &&
                                                review.abuseLastDate &&
                                                review.ableToAbuse && (
                                                    <Tooltip
                                                        title={<Locale stringKey="REVIEWS__REVIEW_HIDDEN_TOOLTIP" />}
                                                    >
                                                        <Button className={styles.ReviewPage_abuseButton} disabled>
                                                            <FontAwesomeIcon icon={faTriangleExclamation} />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            {isHiddenReview &&
                                                !review.deletedAt &&
                                                isAbuseVisibleWhenNotHidden &&
                                                !review.abuseLastDate && (
                                                    <Tooltip
                                                        title={<Locale stringKey="REVIEWS__REVIEW_HIDDEN_TOOLTIP" />}
                                                    >
                                                        <Button disabled style={{width: '100%'}}>
                                                            <Locale stringKey="REVIEWS__REVIEW_ABUSE__HINT" />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                        </>
                                    )}
                                </Flex>
                                {isOpenNoteModal && (
                                    <NoteModal
                                        isLoading={noteMutationLoading}
                                        onCancel={setIsOpenNoteModal}
                                        onSubmit={onSubmit}
                                        reviewId={reviewId}
                                        title={<Locale stringKey="REVIEWS__NOTE_MODAL__TITLE" />}
                                    />
                                )}
                            </div>
                        </div>

                        {isOpenHistoryModal && (
                            <ReviewHistoryModal
                                onCancel={() => setIsOpenHistoryModal(false)}
                                replyTimeInCatalog={review?.replyTimeInCatalog}
                                replyTimeInRd={review?.replyTimeInRd}
                                reviewAuthor={review?.originUrl ? null : review?.author}
                                reviewCreatedInCatalog={review.createdInCatalog}
                                reviewId={reviewId}
                            />
                        )}

                        {isOpenResponsibleUserModal && reviewWithCatalog && (
                            <ResponsibleUserModal
                                initialSelectedUserId={review.responsibleUser?.id}
                                onCancel={() => {
                                    setIsOpenResponsibleUserModal(false);
                                }}
                                reviewData={reviewWithCatalog}
                                reviewId={reviewId}
                                updateReview={(_reviewId, updateData) => updateReview(updateData)}
                            />
                        )}
                        {isOpenAbuseModal && review && (
                            <NewAbuseModal
                                brandId={review.brand.id}
                                catalogId={catalog?.id}
                                refreshReview={(_value) => refreshReviewItem()}
                                reviewId={reviewId}
                                setOpen={setIsOpenAbuseModal}
                            />
                        )}
                    </div>
                )}
            </div>
        </Page>
    );
}
